import {
	Box,
	ButtonBase,
	Slider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import type { FC } from "react";
import {
	WHAT_IF_EXTRA_MAX,
	WHAT_IF_EXTRA_PRESETS,
	WHAT_IF_EXTRA_STEP,
} from "web_myplann/Goals/constants";
import { edgeAwareTransform } from "web_myplann/Goals/Helpers/edgeAwareTransform";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { CurrencyEnum } from "web_myplann/Infrastructure/Api/Api";

type Props = {
	value: number;
	onChange: (value: number) => void;
	currencyCode?: CurrencyEnum;
};

export const WhatIfSlider: FC<Props> = ({ value, onChange, currencyCode }) => {
	const theme = useTheme();
	const formatAmount = useFormatGoalAmount();

	const pct = (value / WHAT_IF_EXTRA_MAX) * 100;
	const valueTransform = edgeAwareTransform(pct);

	return (
		<Stack gap={1.6}>
			<Stack direction="row" gap={0.8}>
				{WHAT_IF_EXTRA_PRESETS.map((preset) => {
					const selected = value === preset;
					return (
						<ButtonBase
							key={preset}
							onClick={() => onChange(selected ? 0 : preset)}
							sx={{
								flex: 1,
								py: 0.8,
								borderRadius: theme.radii.lg,
								bgcolor: selected
									? theme.palette.blue.main
									: theme.palette.grey[100],
								transition: "background-color 150ms ease-out",
							}}
						>
							<Typography
								variant="button"
								color={
									selected
										? theme.palette.darkmodeText.white
										: theme.palette.grey[600]
								}
								tabular
								noWrap
								textTransform="none"
								lineHeight={1.4}
							>
								{`+${formatAmount(preset, currencyCode)}`}
							</Typography>
						</ButtonBase>
					);
				})}
			</Stack>

			<Box px={0.5}>
				<Slider
					variant="flat"
					value={value}
					onChange={(_, v) => onChange(typeof v === "number" ? v : v[0])}
					min={0}
					max={WHAT_IF_EXTRA_MAX}
					step={WHAT_IF_EXTRA_STEP}
				/>
				<Box position="relative" height={18}>
					<Typography
						variant="hint"
						tabular
						position="absolute"
						left={0}
						top={0}
						sx={{
							opacity: value > 0 && pct < 20 ? 0 : 1,
							transition: "opacity 150ms ease-out",
						}}
					>
						{formatAmount(0, currencyCode)}
					</Typography>
					{value > 0 && (
						<Typography
							variant="bodyStrong"
							color={theme.palette.blue.main}
							tabular
							noWrap
							position="absolute"
							top={0}
							left={`${pct}%`}
							sx={{ transform: valueTransform }}
						>
							{`+${formatAmount(value, currencyCode)}`}
						</Typography>
					)}
					<Typography
						variant="hint"
						tabular
						position="absolute"
						right={0}
						top={0}
						sx={{
							opacity: pct > 80 ? 0 : 1,
							transition: "opacity 150ms ease-out",
						}}
					>
						{formatAmount(WHAT_IF_EXTRA_MAX, currencyCode)}
					</Typography>
				</Box>
			</Box>
		</Stack>
	);
};
